import { isEqual } from 'lodash-es';

import { type CourseState } from '@editor2/types/courseState';
import {
	type UnsavedChangesPayload,
	type LectureSlideDeckPayload
} from '@editor2/types/unsavedChangesPayload';
import { ElementType, FigureSubType, FigureType } from '@editor2/types/pageElements';
import { getPopupQuizPayload } from '@editor2/services/popupQuizService';
import { type Changes } from '@editor2/types/changes';
import { ChangesStatus } from '@editor2/types/changesStatus';
import { chapterToChapterProperties } from '@editor2/mappers/chapter';
import { pageGroupToPageGroupProperties } from '@editor2/mappers/pageGroup';
import { pageToPageProperties } from '@editor2/mappers/page';
import { bookToEditorFormProperties } from '@editor2/mappers/book';
import { sanitizeValidations } from './elementValidationsService';

const parseEmbedForVideoId = (payload) => {
	// grab video id - anything between view.vzaar.com/ and /
	const videoId = (payload.match(/view\.vzaar\.com\/([^/]+)/) || [])[1];
	return videoId || '';
};

const changesInElements = ({ pageId, state }) => {
	const { elements } = state.pages.entities[pageId];
	return elements && elements.some((elementId) => !!state.changes.elements[elementId]?.length);
};

const changesInPages = (payload) => {
	const { chapterId, pageGroupId, state } = payload;
	const { pages } = pageGroupId
		? state.page_groups.entities[pageGroupId]
		: state.chapters.entities[chapterId];

	return pages.some(
		(pageId) => !!state.changes.pages[pageId]?.length || changesInElements({ pageId, state })
	);
};

const changesInPageGroups = ({ chapterId, state }) => {
	const { page_groups } = state.chapters.entities[chapterId];
	return page_groups.some(
		(pageGroupId) =>
			!!state.changes.page_groups[pageGroupId]?.length || changesInPages({ pageGroupId, state })
	);
};

const getAttributesChanges = ({ statuses, initEntity, currentEntity, mapper }) => {
	switch (true) {
		case statuses?.includes(ChangesStatus.New):
			return mapper ? mapper(currentEntity) : currentEntity; // pass the whole

		case statuses?.includes(ChangesStatus.Modified): {
			const currentAttributes = mapper ? mapper(currentEntity) : currentEntity; // pass only changes
			const initAttributes = mapper ? mapper(initEntity) : initEntity;

			return Object.keys(currentAttributes).reduce((acc, curr) => {
				return isEqual(currentAttributes[curr], initAttributes[curr])
					? acc
					: { ...acc, [curr]: currentAttributes[curr] };
			}, {});
		}
		default:
			return {};
	}
};

const getChoicesChanges = (choices, initElement) => {
	return choices.map((choice, i) => {
		const initChoice = initElement.choices.find((el) => el.family_id === choice.family_id);

		if (!initChoice) return choice;

		return Object.keys(choice).reduce((acc, curr) => {
			if (curr === 'id') return { ...acc, [curr]: choice[curr] };

			return isEqual(choice[curr], initChoice[curr]) ? acc : { ...acc, [curr]: choice[curr] };
		}, {});
	});
};

const getPoolQuestionsChanges = (questions, initElement) => {
	return questions.map((question) => {
		const initQuestion = initElement.questions.find((el) => el.family_id === question.family_id);
		if (!initQuestion) return question;

		return Object.keys(question).reduce((acc, curr) => {
			if (curr === 'id') return { ...acc, [curr]: question[curr] };
			if (curr === 'choices')
				return { ...acc, [curr]: getChoicesChanges(question[curr], initQuestion) };

			return isEqual(question[curr], initQuestion[curr]) ? acc : { ...acc, [curr]: question[curr] };
		}, {});
	});
};

const postprocessBuilderPayload = (payload) => {
	if ('config' in payload && !payload.config) {
		return { ...payload, config: '{}' };
	}
	return payload;
};

const postprocessTimelinePayload = (payload) => {
	if (!payload?.data?.events?.length) return payload;

	// don't pass end date if it's empty
	const updatedEvents = payload.data.events.map((slide) => {
		if (!slide.end_date) return slide;

		const isEndDateEmpty = Object.keys(slide.end_date).length === 0;

		if (isEndDateEmpty) {
			const { end_date, ...slideProperties } = slide;
			return slideProperties;
		}

		return slide;
	});
	return { ...payload, data: { ...payload.data, events: updatedEvents } };
};

const createElementsPayload = ({ pageId, state }) => {
	const { elements } = state.pages.entities[pageId];

	const elementsReorder = state.changes.pages[pageId]?.includes(ChangesStatus.ChildrenReorder);
	const passAllElements = changesInElements({ pageId, state }) || elementsReorder;

	if (!elements || !passAllElements) return null;

	// eslint-disable-next-line complexity
	const elementsPayload = elements.map((elementId) => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const {
			indent,
			initPageId,
			isGeneratingQuestions,
			needsQuestionFeedback,
			questionGenerationPayload,
			questionGenerationResponse,
			isGeneratingConcepts,
			...elementEntity
		} = state.elements.entities[elementId];
		let element = elementEntity;
		let initElement = state.initialCourse.elements.entities[elementId];
		const statuses = state.changes.elements[elementId];

		if (element.type === ElementType.PopupQuiz) {
			element = getPopupQuizPayload(element);
			initElement = initElement && getPopupQuizPayload(initElement);
		}

		let attributesPayload;

		if (element.type === ElementType.SAQuestion || element.type === ElementType.YourTurn) {
			// we only send back exact-match validations if they've been overridden on the
			// element, because it's all or nothing, and we don't allow edits on config
			// validations.
			const { validations, exactMatchOverride, ...attributes } = element;
			attributesPayload = getAttributesChanges({
				statuses,
				initEntity: initElement,
				currentEntity: attributes,
				mapper: null
			});

			const isNewOrModified =
				statuses?.includes(ChangesStatus.Modified) || statuses?.includes(ChangesStatus.New);
			const sanitizedValidations = sanitizeValidations(validations);

			attributesPayload = {
				...attributesPayload,
				...(isNewOrModified &&
					exactMatchOverride && {
						validations: sanitizedValidations.filter((validation) => {
							return validation.name !== 'exact-match' || validation.source === 'element';
						})
					}),
				...(isNewOrModified &&
					!exactMatchOverride && {
						validations: sanitizedValidations.filter((validation) => {
							return validation.name !== 'exact-match';
						})
					})
			};
		} else if (element.type === ElementType.MCQuestion) {
			const { choices, ...attributes } = element;

			attributesPayload = getAttributesChanges({
				statuses,
				initEntity: initElement,
				currentEntity: attributes,
				mapper: null
			});

			attributesPayload = {
				...attributesPayload,
				...(statuses?.includes(ChangesStatus.Modified) &&
					!statuses?.includes(ChangesStatus.New) && {
						choices: getChoicesChanges(choices, initElement)
					}),
				...(statuses?.includes(ChangesStatus.New) && { choices: choices })
			};
		} else if (element.type === ElementType.MCQuestionPool) {
			const { questions, ...attributes } = element;

			attributesPayload = getAttributesChanges({
				statuses,
				initEntity: initElement,
				currentEntity: attributes,
				mapper: null
			});

			attributesPayload = {
				...attributesPayload,
				...(statuses?.includes(ChangesStatus.Modified) &&
					!statuses?.includes(ChangesStatus.New) && {
						questions: getPoolQuestionsChanges(questions, initElement)
					}),
				...(statuses?.includes(ChangesStatus.New) && { questions: questions })
			};
		} else if (
			element.type === ElementType.Figure &&
			element.figure_type === FigureType.video &&
			element.figure_subtype === FigureSubType.vzaar
		) {
			attributesPayload = getAttributesChanges({
				statuses,
				initEntity: initElement,
				currentEntity: element,
				mapper: null
			});

			// update vzaar_id when payload changes
			if (attributesPayload.payload) {
				attributesPayload = {
					...attributesPayload,
					payload_metadata: {
						...element.payload_metadata,
						vzaar_id: parseEmbedForVideoId(element.payload)
					}
				};
			}
		} else if (element.type === ElementType.Timeline) {
			const payload = getAttributesChanges({
				statuses,
				initEntity: initElement,
				currentEntity: element,
				mapper: null
			});

			attributesPayload = postprocessTimelinePayload(payload);
		} else if (element.type === ElementType.Builder) {
			const payload = getAttributesChanges({
				statuses,
				initEntity: initElement,
				currentEntity: element,
				mapper: null
			});

			attributesPayload = postprocessBuilderPayload(payload);
		} else {
			attributesPayload = getAttributesChanges({
				statuses,
				initEntity: initElement,
				currentEntity: element,
				mapper: null
			});
		}

		const pagePayload = {
			...(!!element.id && { id: element.id }),
			...attributesPayload
		};

		return pagePayload;
	});

	return elementsPayload;
};

const createPagesPayload = (payload) => {
	const { chapterId, pageGroupId, state } = payload;
	const { pages } = pageGroupId
		? state.page_groups.entities[pageGroupId]
		: state.chapters.entities[chapterId];

	const pagesReorder = pageGroupId
		? state.changes.page_groups[pageGroupId]?.includes(ChangesStatus.ChildrenReorder)
		: state.changes.chapters[chapterId]?.includes(ChangesStatus.ChildrenReorder);
	const passAllPages = changesInPages({ chapterId, pageGroupId, state }) || pagesReorder;

	if (!passAllPages) return null;

	const pagesPayload = pages.map((pageId) => {
		const { currentPageNumber, ...page } = state.pages.entities[pageId];
		const initPage = state.initialCourse.pages.entities[pageId];
		const statuses = state.changes.pages[pageId];

		const elementsPayload = createElementsPayload({ pageId, state });

		const attributesPayload = getAttributesChanges({
			statuses,
			initEntity: initPage,
			currentEntity: page,
			mapper: pageToPageProperties
		});

		const pagePayload = {
			...(!!page.id && { id: page.id }),
			...attributesPayload,
			...(!!elementsPayload && { elements: elementsPayload })
		};

		return pagePayload;
	});

	return pagesPayload;
};

const createPageGroupsPayload = ({ chapterId, state }) => {
	const { page_groups } = state.chapters.entities[chapterId];

	const pageGroupsReorder = state.changes.chapters[chapterId]?.includes(
		ChangesStatus.ChildrenReorder
	);
	const passAllPageGroups = changesInPageGroups({ chapterId, state }) || pageGroupsReorder;

	if (!passAllPageGroups) return null;

	const pageGroupsPayload = page_groups.map((pageGroupId) => {
		const pageGroup = state.page_groups.entities[pageGroupId];
		const initPageGroup = state.initialCourse.page_groups.entities[pageGroupId];
		const statuses = state.changes.page_groups[pageGroupId];

		const pagesPayload = createPagesPayload({ pageGroupId, state });

		const attributesPayload = getAttributesChanges({
			statuses,
			initEntity: initPageGroup,
			currentEntity: pageGroup,
			mapper: pageGroupToPageGroupProperties
		});

		const pageGroupPayload = {
			...(!!pageGroup.id && { id: pageGroup.id }),
			...attributesPayload,
			...(!!pagesPayload && { pages: pagesPayload })
		};

		return pageGroupPayload;
	});

	return pageGroupsPayload;
};

const createChaptersPayload = (state: CourseState) => {
	const chaptersPayload = state.chapters.ids.map((chapterId) => {
		const chapter = state.chapters.entities[chapterId];
		const initChapter = state.initialCourse.chapters.entities[chapterId];
		const statuses = state.changes.chapters[chapterId];

		const pageGroupsPayload = chapter.page_groups_enabled
			? createPageGroupsPayload({ chapterId, state })
			: null;

		const pagesPayload = !chapter.page_groups_enabled
			? createPagesPayload({ chapterId, state })
			: null;

		const attributesPayload = getAttributesChanges({
			statuses,
			initEntity: initChapter,
			currentEntity: chapter,
			mapper: chapterToChapterProperties
		});

		const chapterPayload = {
			...(!!chapter.id && { id: chapter.id }),
			...attributesPayload,
			...(!!pageGroupsPayload && { page_groups: pageGroupsPayload }),
			...(!!pagesPayload && { pages: pagesPayload })
		};

		return chapterPayload;
	});

	return chaptersPayload;
};

const createCourseResourcePayload = (state: CourseState) => {
	const book = state.book;
	const initBook = state.initialCourse.book;

	const editorFormPayload = getAttributesChanges({
		statuses: state.changes.toc,
		initEntity: initBook,
		currentEntity: book,
		mapper: bookToEditorFormProperties
	});

	const yamlPayload =
		book.yaml_config !== initBook.yaml_config ? { yaml_config: book.yaml_config } : {};

	return { ...editorFormPayload, ...yamlPayload };
};

export const createChangesPayload = (state: CourseState) => {
	const bookChanges = !!state.changes.toc.length || !!state.changes.resourceConfig.length;
	const chaptersChanges = checkUnsavedChaptersChanges(state.changes);
	const lectureSlideDecksChanges =
		checkUnsavedLecturesSlideDecksChanges(state.changes) ||
		checkLectureSlideDecksOrderChanged(state.changes);

	const payload: UnsavedChangesPayload = {
		course: { id: state.course.id },
		book: {
			...(bookChanges && createCourseResourcePayload(state)),
			...(chaptersChanges && { chapters: createChaptersPayload(state) }),
			...(lectureSlideDecksChanges && {
				lecture_slide_decks: createLectureSlideDecksPayload(state)
			})
		}
	};

	return payload;
};

const createLectureSlideDecksPayload = (state: CourseState): LectureSlideDeckPayload[] | null => {
	const hasDeckChanges = Object.values(state.changes.lecture_slide_decks ?? {}).some(
		(statuses) => !!statuses?.length
	);
	if (!hasDeckChanges) return null;

	return state.lecture_slide_decks.ids.flatMap((deckId) => {
		const deck = state.lecture_slide_decks.entities[deckId];
		const statuses = state.changes.lecture_slide_decks[deckId];

		if (statuses?.includes(ChangesStatus.Deleted)) return [];

		if (statuses?.includes(ChangesStatus.New)) {
			return [
				{
					family_id: deck.family_id,
					associated_family_ids: deck.associated_family_ids,
					self_serve_sample: deck.self_serve_sample,
					...(deck.file && {
						file: { id: deck.file.id, ...(deck.file.notes ? { notes: deck.file.notes } : {}) }
					})
				}
			];
		}

		if (statuses?.includes(ChangesStatus.Modified)) {
			const initDeck = state.initialCourse.lecture_slide_decks?.entities[deckId];
			const changes: LectureSlideDeckPayload = { id: deck.id };
			if (!isEqual(deck.associated_family_ids, initDeck?.associated_family_ids)) {
				changes.associated_family_ids = deck.associated_family_ids;
			}
			if (deck.self_serve_sample !== initDeck?.self_serve_sample) {
				changes.self_serve_sample = deck.self_serve_sample;
			}
			if (deck.file) {
				const initFile = initDeck?.file;
				const fileChanges: Record<string, unknown> = {};
				if (deck.file.notes !== initFile?.notes) fileChanges.notes = deck.file.notes ?? '';
				if (deck.file.file_uuid && deck.file.file_uuid !== initFile?.file_uuid) {
					fileChanges.file_uuid = deck.file.file_uuid;
					fileChanges.original_name = deck.file.original_name;
					fileChanges.filesize_bytes = deck.file.filesize_bytes;
					fileChanges.checksum_sha256 = deck.file.checksum_sha256;
					fileChanges.uploader_id = deck.file.uploader_id;
					fileChanges.uploaded_at = deck.file.uploaded_at;
				}
				if (Object.keys(fileChanges).length > 0) {
					changes.file = { id: deck.file.id, ...fileChanges } as LectureSlideDeckPayload['file'];
				}
			}
			return [changes];
		}

		// Unchanged — send only the id as a no-op marker
		if (deck.id) return [{ id: deck.id }];
		return [];
	});
};

const checkUnsavedChaptersChanges = (changes: Changes) => {
	return (
		changes.toc.includes(ChangesStatus.ChildrenReorder) ||
		Object.values(changes.chapters).some((statuses) => !!statuses.length) ||
		Object.values(changes.page_groups).some((statuses) => !!statuses.length) ||
		Object.values(changes.pages).some((statuses) => !!statuses.length) ||
		Object.values(changes.elements).some((statuses) => !!statuses.length)
	);
};

const checkUnsavedLecturesSlideDecksChanges = (changes: Changes) => {
	return Object.entries(changes.lecture_slide_decks ?? {}).some(
		([key, statuses]) => !!statuses?.length && key !== '__order__'
	);
};

export const checkLectureSlideDecksOrderChanged = (changes: Changes) => {
	return !!changes.lecture_slide_decks?.['__order__']?.length;
};

export const checkUnsavedTocChanges = (changes: Changes) => {
	return !!changes.toc.length || checkUnsavedChaptersChanges(changes);
};

export const checkUnsavedConfigChanges = (changes: Changes) => {
	return !!changes.resourceConfig.length;
};

export const checkUnsavedFileChanges = (changes: Changes) =>
	checkUnsavedLecturesSlideDecksChanges(changes) || checkLectureSlideDecksOrderChanged(changes);

export const hasAnyActiveChanges = (changes: Changes) =>
	checkUnsavedTocChanges(changes) ||
	checkUnsavedConfigChanges(changes) ||
	checkUnsavedFileChanges(changes);
